/**
 * AudioManager - Centralized audio handling
 *
 * Manages background music, sound effects, and volume controls.
 * Will be extracted from main.js in the next step.
 */

import { AUDIO, STORAGE_KEYS } from '../game/config.js';

export const AUDIO_KIND_MUSIC = 'music';
export const AUDIO_KIND_SFX = 'sfx';

export class AudioManager {
  constructor(musicTracks = []) {
    this.managedAudio = new Set();
    this.musicAudio = null;
    this.musicFadeCancel = null;
    this.musicWasPlaying = false;
    this.musicMuted = false;
    this.allMuted = false;

    // Music playback state
    this.musicTracks = musicTracks;
    this.musicNextTimer = null;
    this.musicLastIndex = -1;
    this.musicNeedsUserGesture = false;
    this.musicCrossfadeStarted = false;
    this.musicCrossfadeSeconds = 3; // Seconds before end to start crossfade

    // Simple pool for frequently used SFX
    this.sfxPool = new Map(); // Map<src, Audio[]>

    // Centralized audio registry - Map<name, {audio, src, loading, loaded}>
    this.namedAudio = new Map();

    // Audio preloading queue
    this.preloadQueue = [];
    this.isPreloading = false;

    // Volume settings
    this.volumeSettings = {
      master: this.loadVolumeSetting(STORAGE_KEYS.VOLUME_MASTER, AUDIO.MASTER_VOLUME),
      music: this.loadVolumeSetting(STORAGE_KEYS.VOLUME_MUSIC, 1.0),
      sfx: this.loadVolumeSetting(STORAGE_KEYS.VOLUME_SFX, 1.0),
    };

    // Callbacks for audio unlock UI
    this.onAudioUnlockNeeded = null;
    this.onAudioUnlocked = null;
  }

  /**
   * Load volume setting from localStorage
   */
  loadVolumeSetting(key, fallback) {
    try {
      const stored = localStorage.getItem(key);
      if (stored != null) {
        const parsed = parseFloat(stored);
        if (Number.isFinite(parsed)) return this.clamp01(parsed);
      }
    } catch (e) {
      console.warn(`Failed to load volume setting ${key}:`, e);
    }
    return this.clamp01(fallback);
  }

  /**
   * Save volume setting to localStorage
   */
  saveVolumeSetting(key, value) {
    try {
      localStorage.setItem(key, String(this.clamp01(value)));
    } catch (e) {
      console.warn(`Failed to save volume setting ${key}:`, e);
    }
  }

  /**
   * Clamp value between 0 and 1
   */
  clamp01(v) {
    return Math.min(1, Math.max(0, Number.isFinite(v) ? v : 0));
  }

  /**
   * Set base volume for an audio element and track it
   */
  setAudioBaseVolume(audio, base, kind = AUDIO_KIND_SFX) {
    if (!audio) return;
    audio.__volumeBase = this.clamp01(base);
    audio.__volumeKind = (kind === AUDIO_KIND_MUSIC) ? AUDIO_KIND_MUSIC : AUDIO_KIND_SFX;
    this.managedAudio.add(audio);
    this.applyAudioVolume(audio);
  }

  /**
   * Refresh all managed audio volumes
   */
  refreshManagedAudioVolumes() {
    this.managedAudio.forEach(audio => this.applyAudioVolume(audio));
  }

  /**
   * Set master volume
   */
  setMasterVolume(value) {
    this.volumeSettings.master = this.clamp01(value);
    this.saveVolumeSetting(STORAGE_KEYS.VOLUME_MASTER, value);
    this.refreshManagedAudioVolumes();
  }

  /**
   * Set music volume
   */
  setMusicVolume(value) {
    this.volumeSettings.music = this.clamp01(value);
    this.saveVolumeSetting(STORAGE_KEYS.VOLUME_MUSIC, value);
    this.refreshManagedAudioVolumes();
  }

  /**
   * Set SFX volume
   */
  setSfxVolume(value) {
    this.volumeSettings.sfx = this.clamp01(value);
    this.saveVolumeSetting(STORAGE_KEYS.VOLUME_SFX, value);
    this.refreshManagedAudioVolumes();
  }

  /**
   * Fade audio element to target volume
   */
  fadeAudio(audio, toBase, ms, kind = AUDIO_KIND_MUSIC) {
    if (!audio) return () => {};
    const target = this.clamp01(toBase);
    const startBase = (typeof audio.__volumeBase === 'number') ? audio.__volumeBase : this.clamp01(audio.volume);

    if (ms <= 0) {
      this.cancelMusicFade();
      this.setAudioBaseVolume(audio, target, kind);
      this.musicFadeCancel = null;
      return () => {};
    }

    const start = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    let raf = 0;
    const step = (now) => {
      const t = Math.min(1, (now - start) / ms);
      const base = startBase + (target - startBase) * t;
      this.setAudioBaseVolume(audio, base, kind);
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        raf = 0;
        this.musicFadeCancel = null;
      }
    };

    this.cancelMusicFade();
    raf = requestAnimationFrame(step);
    this.musicFadeCancel = () => {
      if (raf) cancelAnimationFrame(raf);
      raf = 0;
      this.musicFadeCancel = null;
    };
    return this.musicFadeCancel;
  }

  /**
   * Cancel current music fade
   */
  cancelMusicFade() {
    if (typeof this.musicFadeCancel === 'function') {
      this.musicFadeCancel();
    }
  }

  /**
   * Music time update callback - handle crossfade
   */
  onMusicTimeUpdate = () => {
    if (!this.musicAudio || this.musicCrossfadeStarted) return;

    // Don't trigger crossfade if underground music is active (prevents jitter in caves)
    const undergroundMusicAudio = this.namedAudio.get('undergroundMusic')?.audio;
    if (undergroundMusicAudio && !undergroundMusicAudio.paused) {
      return; // Underground music is playing, don't crossfade main music
    }

    const duration = this.musicAudio.duration;
    const currentTime = this.musicAudio.currentTime;

    // Check if we're within crossfade window (last N seconds)
    if (duration && currentTime && (duration - currentTime <= this.musicCrossfadeSeconds)) {
      this.musicCrossfadeStarted = true;
      // Start fading out current track
      this.fadeAudio(this.musicAudio, 0.0, this.musicCrossfadeSeconds * 1000, AUDIO_KIND_MUSIC);
      // Queue next track to start after a short delay
      clearTimeout(this.musicNextTimer);
      this.musicNextTimer = setTimeout(() => {
        this.playRandomTrack();
      }, this.musicCrossfadeSeconds * 500); // Start halfway through fadeout for overlap
    }
  }

  /**
   * Music ended callback - play next random track
   */
  onMusicEnded = () => {
    // Reset crossfade flag
    this.musicCrossfadeStarted = false;
    // Don't start next track if underground music is active (prevents jitter in caves)
    const undergroundMusicAudio = this.namedAudio.get('undergroundMusic')?.audio;
    if (undergroundMusicAudio && !undergroundMusicAudio.paused) {
      return; // Underground music is playing, don't start main music
    }
    // If crossfade didn't trigger (short track or seeking), play next immediately
    if (!this.musicNextTimer) {
      this.playRandomTrack();
    }
  }

  /**
   * Play random music track with fade-in
   */
  playRandomTrack() {
    if (!this.musicAudio || this.musicTracks.length === 0) return;

    const n = this.musicTracks.length;
    let idx = Math.floor(Math.random() * n);
    if (n > 1 && idx === this.musicLastIndex) {
      idx = (idx + 1) % n;
    }
    this.musicLastIndex = idx;
    this.musicCrossfadeStarted = false; // Reset crossfade flag for new track

    this.musicAudio.src = this.musicTracks[idx];
    this.setAudioBaseVolume(this.musicAudio, 0.0, AUDIO_KIND_MUSIC);

    const p = this.musicAudio.play();
    if (p && typeof p.then === 'function') {
      // Single chain: fade in on success, install a gesture unlock if autoplay is blocked.
      // (Do NOT branch p.then and p.catch separately — the then-branch would be an
      // unhandled rejection when play() rejects.)
      p.then(() => {
        this.fadeAudio(this.musicAudio, AUDIO.MUSIC_BASE_VOLUME, 280, AUDIO_KIND_MUSIC);
      }).catch(() => {
        this.musicNeedsUserGesture = true;
        this.installMusicGestureUnlock();
      });
    } else {
      this.fadeAudio(this.musicAudio, AUDIO.MUSIC_BASE_VOLUME, 280, AUDIO_KIND_MUSIC);
    }
  }

  /**
   * Play next music track in sequence (for manual track cycling)
   */
  playNextTrack() {
    if (!this.musicAudio || this.musicTracks.length === 0) return;

    clearTimeout(this.musicNextTimer);
    const n = this.musicTracks.length;
    this.musicLastIndex = (this.musicLastIndex + 1) % n;
    this.musicCrossfadeStarted = false; // Reset crossfade flag for new track

    this.musicAudio.src = this.musicTracks[this.musicLastIndex];
    this.setAudioBaseVolume(this.musicAudio, 0.0, AUDIO_KIND_MUSIC);

    const p = this.musicAudio.play();
    if (p && typeof p.then === 'function') {
      // Single chain (see playRandomTrack): avoid an unhandled rejection on autoplay block.
      p.then(() => {
        this.fadeAudio(this.musicAudio, AUDIO.MUSIC_BASE_VOLUME, 280, AUDIO_KIND_MUSIC);
      }).catch(() => {
        this.musicNeedsUserGesture = true;
        this.installMusicGestureUnlock();
      });
    } else {
      this.fadeAudio(this.musicAudio, AUDIO.MUSIC_BASE_VOLUME, 280, AUDIO_KIND_MUSIC);
    }
  }

  /**
   * Install gesture unlock for autoplay-blocked music
   */
  installMusicGestureUnlock() {
    // Notify UI that audio unlock is needed
    if (this.onAudioUnlockNeeded) {
      this.onAudioUnlockNeeded();
    }

    const unlock = () => {
      if (!this.musicNeedsUserGesture) return;
      this.musicNeedsUserGesture = false;
      if (this.musicAudio && this.musicAudio.paused) {
        const p = this.musicAudio.play();
        if (p && typeof p.then === 'function') {
          p.then(() => {
            this.fadeAudio(this.musicAudio, AUDIO.MUSIC_BASE_VOLUME, 280, AUDIO_KIND_MUSIC);
          }).catch(() => {});
        }
      }
      // Notify UI that audio was unlocked
      if (this.onAudioUnlocked) {
        this.onAudioUnlocked();
      }
      document.removeEventListener('click', unlock);
      document.removeEventListener('touchend', unlock);
      document.removeEventListener('keydown', unlock);
    };
    // Any of these counts as the user gesture that unlocks audio (keyboard included, so the
    // prompt dismisses when a keyboard player presses a key — not just on click/tap).
    document.addEventListener('click', unlock, { once: true });
    document.addEventListener('touchend', unlock, { once: true });
    document.addEventListener('keydown', unlock, { once: true });
  }

  /**
   * Start background music playback
   */
  startBackgroundMusic() {
    if (!this.musicAudio) {
      this.musicAudio = new Audio();
      this.musicAudio.preload = 'metadata';
      this.musicAudio.loop = false;
      this.musicAudio.addEventListener('ended', this.onMusicEnded);
      this.musicAudio.addEventListener('timeupdate', this.onMusicTimeUpdate); // For crossfade
    }
    this.setAudioBaseVolume(this.musicAudio, AUDIO.MUSIC_BASE_VOLUME, AUDIO_KIND_MUSIC);
    this.musicAudio.muted = this.musicMuted;
    this.musicWasPlaying = true;
    this.playRandomTrack();
  }

  /**
   * Pause background music with fade-out
   */
  pauseBackgroundMusic() {
    if (!this.musicAudio) return;
    this.cancelMusicFade();
    try { clearTimeout(this.musicNextTimer); } catch {}
    this.musicWasPlaying = !this.musicAudio.paused && !this.musicAudio.muted;
    if (!this.musicAudio.paused) {
      this.fadeAudio(this.musicAudio, 0.0, 200, AUDIO_KIND_MUSIC);
      setTimeout(() => {
        try { this.musicAudio.pause(); } catch {}
      }, 210);
    }
  }

  /**
   * Resume background music with fade-in
   */
  resumeBackgroundMusic() {
    if (!this.musicAudio || this.musicMuted) return;
    try { clearTimeout(this.musicNextTimer); } catch {}
    this.musicWasPlaying = true;

    const play = () => {
      const p = this.musicAudio.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          this.musicNeedsUserGesture = true;
          this.installMusicGestureUnlock();
        });
      }
    };

    if (this.musicAudio.src) {
      this.setAudioBaseVolume(this.musicAudio, 0.0, AUDIO_KIND_MUSIC);
      play();
      this.fadeAudio(this.musicAudio, AUDIO.MUSIC_BASE_VOLUME, 220, AUDIO_KIND_MUSIC);
    } else {
      this.startBackgroundMusic();
    }
  }

  /**
   * Toggle mute for all music
   */
  setMusicMuted(muted) {
    this.musicMuted = muted;
    if (this.musicAudio) {
      this.musicAudio.muted = muted;
    }
  }

  /**
   * Get current mute state
   */
  isMusicMuted() {
    return this.musicMuted;
  }

  /**
   * Mute all sounds (music, SFX, ambiance)
   */
  setAllMuted(muted) {
    this.allMuted = muted;
    // Mute all managed audio
    this.managedAudio.forEach(audio => {
      try {
        audio.muted = muted;
      } catch (e) {
        console.warn('Failed to mute audio:', e);
      }
    });
    // Also mute music
    if (this.musicAudio) {
      this.musicAudio.muted = muted || this.musicMuted;
    }
  }

  /**
   * Get current all mute state
   */
  isAllMuted() {
    return this.allMuted;
  }

  /**
   * Override applyAudioVolume to respect all mute
   */
  applyAudioVolume(audio) {
    if (!audio) return;
    const base = (typeof audio.__volumeBase === 'number') ? audio.__volumeBase : this.clamp01(audio.volume);
    const kind = (audio.__volumeKind === AUDIO_KIND_MUSIC) ? AUDIO_KIND_MUSIC : AUDIO_KIND_SFX;
    const master = this.volumeSettings.master;
    const category = (kind === AUDIO_KIND_MUSIC) ? this.volumeSettings.music : this.volumeSettings.sfx;
    const target = this.clamp01(base * master * category);
    try {
      audio.volume = target;
      // Apply mute state
      audio.muted = this.allMuted || (kind === AUDIO_KIND_MUSIC && this.musicMuted);
    } catch (e) {
      console.warn('Failed to set audio volume:', e);
    }
  }

  /**
   * Play sound effect
   */
  playSfx(src, volume = 1.0) {
    let pool = this.sfxPool.get(src);
    if (!pool) {
      pool = [];
      this.sfxPool.set(src, pool);
    }
    let audio = pool.find(a => a.paused);
    if (!audio) {
      audio = new Audio(src);
      this.setAudioBaseVolume(audio, volume, AUDIO_KIND_SFX);
      pool.push(audio);
      // Keep pool size reasonable
      if (pool.length > 6) pool.splice(6);
    } else {
      this.setAudioBaseVolume(audio, volume, AUDIO_KIND_SFX);
    }
    try { audio.currentTime = 0; } catch {}
    const p = audio.play();
    if (p && typeof p.catch === 'function') {
      p.catch((err) => {
        console.warn('SFX playback failed:', err);
      });
    }
    return audio;
  }

  /**
   * Get or create a named audio element
   * Prevents duplicate loading and handles race conditions
   */
  getNamedAudio(name, src, options = {}) {
    const {
      loop = false,
      baseVolume = 1.0,
      kind = AUDIO_KIND_SFX,
      preload = 'auto'
    } = options;

    // Return existing audio if already loaded/loading
    if (this.namedAudio.has(name)) {
      const entry = this.namedAudio.get(name);
      // If source changed, recreate
      if (entry.src === src) {
        return entry.audio;
      }
      // Source changed - cleanup old and create new
      this.removeNamedAudio(name);
    }

    // Create new audio element
    try {
      const audio = new Audio(src);
      audio.loop = loop;
      audio.preload = preload;
      this.setAudioBaseVolume(audio, baseVolume, kind);

      // Register in named audio map
      this.namedAudio.set(name, {
        audio,
        src,
        loading: true,
        loaded: false,
        kind,
        baseVolume
      });

      // Track loading state
      const onCanPlay = () => {
        const entry = this.namedAudio.get(name);
        if (entry) {
          entry.loading = false;
          entry.loaded = true;
        }
        audio.removeEventListener('canplaythrough', onCanPlay);
      };

      const onError = () => {
        console.warn(`Failed to load named audio "${name}" from ${src}`);
        const entry = this.namedAudio.get(name);
        if (entry) {
          entry.loading = false;
          entry.loaded = false;
        }
        audio.removeEventListener('error', onError);
      };

      audio.addEventListener('canplaythrough', onCanPlay, { once: true });
      audio.addEventListener('error', onError, { once: true });

      return audio;
    } catch (err) {
      console.error(`Failed to create named audio "${name}":`, err);
      return null;
    }
  }

  /**
   * Remove and cleanup a named audio element
   */
  removeNamedAudio(name) {
    if (!this.namedAudio.has(name)) return;

    const entry = this.namedAudio.get(name);
    if (entry.audio) {
      try {
        entry.audio.pause();
        entry.audio.currentTime = 0;
        entry.audio.src = ''; // Release resource
      } catch (err) {
        console.warn(`Error cleaning up audio "${name}":`, err);
      }
      this.managedAudio.delete(entry.audio);
    }

    this.namedAudio.delete(name);
  }

  /**
   * Check if named audio is loaded and ready
   */
  isAudioReady(name) {
    const entry = this.namedAudio.get(name);
    return entry && entry.loaded && entry.audio;
  }

  /**
   * Preload audio files in background
   */
  async preloadAudio(audioList) {
    const promises = audioList.map(({ name, src, options }) => {
      return new Promise((resolve) => {
        const audio = this.getNamedAudio(name, src, options);
        if (!audio) {
          resolve(false);
          return;
        }

        const entry = this.namedAudio.get(name);
        if (entry && entry.loaded) {
          resolve(true);
          return;
        }

        const checkLoaded = setInterval(() => {
          const e = this.namedAudio.get(name);
          if (!e || e.loaded || !e.loading) {
            clearInterval(checkLoaded);
            resolve(e && e.loaded);
          }
        }, 100);

        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkLoaded);
          resolve(false);
        }, 10000);
      });
    });

    const results = await Promise.all(promises);
    const successCount = results.filter(r => r).length;
    console.log(`Preloaded ${successCount}/${audioList.length} audio files`);
    return results;
  }

  /**
   * Cleanup all named audio
   */
  cleanupAllNamedAudio() {
    const names = Array.from(this.namedAudio.keys());
    names.forEach(name => this.removeNamedAudio(name));
  }

  /**
   * Stop and reset a named audio element
   */
  stopNamedAudio(name) {
    const entry = this.namedAudio.get(name);
    if (!entry || !entry.audio) return;

    try {
      entry.audio.pause();
      entry.audio.currentTime = 0;
    } catch (err) {
      console.warn(`Error stopping audio "${name}":`, err);
    }
  }

  /**
   * Play a named audio element
   */
  playNamedAudio(name, fadeInMs = 0) {
    const entry = this.namedAudio.get(name);
    if (!entry || !entry.audio) {
      console.warn(`Named audio "${name}" not found`);
      return null;
    }

    const audio = entry.audio;

    if (fadeInMs > 0) {
      this.setAudioBaseVolume(audio, 0, entry.kind);
    }

    const p = audio.play();
    if (p && typeof p.then === 'function') {
      // Single chain: fade in on success, warn on failure. Avoids an unhandled rejection
      // from a separate then-branch when play() is blocked.
      p.then(() => {
        if (fadeInMs > 0) this.fadeAudio(audio, entry.baseVolume, fadeInMs, entry.kind);
      }).catch((err) => {
        console.warn(`Failed to play "${name}":`, err);
      });
    }

    return audio;
  }
}
